// src/components/SimpleComponents/SimpleCascader.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useState, useCallback } from 'react';
import { Button, Cascader, Divider, Flex } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useSimpleFormContext } from './SimpleFormProvider';
import { useModuleContext } from 'components/core/ModuleProvider';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import translateX from 'utils/translateX';
import appService from 'services/appService';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleCascader = ({
  name,
  subdocumentField,
  style = {},
  styleItem = {},
  options = [],
  showSearch = true,
  allowClear = true,
  changeOnSelect = true, // allows selecting any level
  expandTrigger = 'click', // 'click' or 'hover'
  disabled = false,
  placeholder,
  onChange,
  showAddButton = false,
  onAddButtonLabel = 'new',
  onAddButtonFn = () => console.log('onAddButtonFn() not implemented!'),
  displayRender,
  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, disabledFields } = useSimpleFormContext();
  const { openParametersModal } = useModuleContext() || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);
  const i18nToken = appService.getLocale();

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [open, setOpen] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleDropdownVisibleChange = useCallback(visible => {
    setOpen(visible);
  }, []);

  const handleChange = useCallback(
    (value, selectedOptions) => {
      if (typeof onChange === 'function') {
        onChange(value, selectedOptions);
      }
    },
    [onChange]
  );

  // search filter function
  const searchFilter = useCallback((inputValue, path) => {
    return path.some(option => option.label.toLowerCase().includes(inputValue.toLowerCase()));
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const translatedOptions = useMemo(() => $translateOptions(options), [options, i18nToken]);

  // Parse showAddButton config - can be boolean or object { modal: 'parameters', tab: 'categories' }
  const addButtonConfig = useMemo(() => {
    if (!showAddButton) return null;

    // Boolean true - use default onAddButtonFn
    if (showAddButton === true) {
      return {
        show: true,
        onClick: onAddButtonFn,
        label: onAddButtonLabel,
      };
    }

    // Object config - e.g., { modal: 'parameters', tab: 'categories' }
    if (typeof showAddButton === 'object') {
      const { modal, tab } = showAddButton;

      if (modal === 'parameters' && openParametersModal) {
        return {
          show: true,
          onClick: () => openParametersModal(tab || null),
          label: showAddButton.label || 'new',
        };
      }

      // Custom modal - use onAddButtonFn
      return {
        show: true,
        onClick: onAddButtonFn,
        label: showAddButton.label || onAddButtonLabel,
      };
    }

    return null;
  }, [showAddButton, onAddButtonFn, onAddButtonLabel, openParametersModal]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormItem {...props} name={fullFieldName} elementProps={elementProps} style={styleItem}>
      <Cascader
        key={i18nToken}
        ref={elementRef}
        id={flattenFieldName}
        style={{ width: '100%', ...style }}
        popupMatchSelectWidth
        options={translatedOptions}
        showSearch={showSearch ? { filter: searchFilter } : false}
        allowClear={allowClear}
        changeOnSelect={changeOnSelect}
        expandTrigger={expandTrigger}
        disabled={isDisabled}
        placeholder={translateX(placeholder)}
        open={open}
        onOpenChange={handleDropdownVisibleChange}
        onChange={handleChange}
        displayRender={displayRender}
        popupRender={
          addButtonConfig?.show
            ? menu => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Flex justify="center" align="center">
                    <Button
                      size="small"
                      style={{ width: '100%' }}
                      type="default"
                      className="m-2 pl-2 pr-3"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setOpen(false);
                        addButtonConfig.onClick();
                      }}
                    >
                      {translateX(addButtonConfig.label)}
                    </Button>
                  </Flex>
                </>
              )
            : undefined
        }
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleCascader;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function $translateOptions(rawOptions = []) {
  // recursively translate labels in cascader options
  return rawOptions.map(opt => ({
    ...opt,
    value: opt.value,
    label: typeof opt.label === 'string' ? translateX(opt.label) : opt.label,
    children: opt.children ? $translateOptions(opt.children) : undefined,
  }));
}
